import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import cytoscape, { type Core, type ElementDefinition } from 'cytoscape';
import {
  getAccessGraph,
  getEgressLineage,
  type AccessGraph,
  type AccessGraphEdge,
  type EgressLineage,
} from '@/lib/api';
import dagre from 'cytoscape-dagre';
import { motion } from 'motion/react';
import {
  GitBranch, Database, ZoomIn, ZoomOut, Maximize, RotateCcw, Info, AlertTriangle, ShieldAlert,
  Globe2, Lock, Layers3, FileWarning, Activity, ArrowRight, Sparkles, Search, MapPin, Cloud, Server,
} from 'lucide-react';
import { deriveLocation } from '@/lib/asset-location';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { EstateMap } from './EstateMap';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VirtualList } from '@/components/ui/virtual-list';
import { PageGuide } from '@/components/ui/page-guide';
import { cn } from '@/lib/cn';
import { useTheme } from '@/lib/theme';
import { DEMO_NODES, DEMO_EDGES, DEMO_EXPOSURE_PATHS } from './Lineage.demo';

cytoscape.use(dagre);

type Cat = 'app' | 'pipeline' | 'storage' | 'analytics' | 'cross-border' | 'export' | 'db';
type Sev = 'critical' | 'high' | 'medium' | 'low';

interface NodeData {
  id: string;
  label: string;
  cat: Cat;
  sev?: Sev;
  pii?: string[];
  rows?: number;
  /** live-graph provenance so the inspector can derive physical location. */
  kind?: 'asset' | 'finding_category';
  sourceType?: string;
  countryCode?: string | null;
}

/**
 * Map a live AccessGraphNode's `kind` + `sourceType` to a presentation
 * `cat` so the Cytoscape stylesheet's category-fill rules can paint
 * the live graph with the same vocabulary as the demo. Best-effort -
 * unknown source types degrade to the generic `db` shape.
 */
function catFromLive(kind: 'asset' | 'finding_category', sourceType: string | undefined): Cat {
  if (kind === 'finding_category') return 'export';
  const s = (sourceType ?? '').toLowerCase();
  // A copybook extract is a file on a share, so it reads as storage rather than
  // as the database the copybook was unloaded from. The default fallthrough
  // below is `db`, which would have put an AS/400 extract next to live tables.
  if (/^s3$|^azure_blob$|^gcs$|^copybook_file$|^ssh_filesystem$|object_storage/.test(s)) return 'storage';
  if (/^snowflake$|^bigquery$|^redshift$|^databricks$/.test(s)) return 'analytics';
  if (/^m365|^gws|^workspace|sharepoint|onedrive/.test(s)) return 'app';
  if (/^postgres$|^mysql$|^mssql$|^oracle$|^mongodb$|^db2$|^db2i$|^mariadb$/.test(s)) return 'db';
  return 'db';
}

/** Pick the most severe of two strings ranked by enum order. */
function maxSev(a: Sev | undefined, b: string): Sev | undefined {
  const rank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const ra = a ? rank[a] ?? 0 : 0;
  const rb = rank[b] ?? 0;
  if (rb > ra) return b as Sev;
  return a;
}

/**
 * Pick a representative principal identifier to seed a subject-data search from an
 * asset node, so "Find a person's data from here" lands on a POPULATED traversal
 * instead of an empty form. We infer the asset's dominant PII from the finding
 * categories linked to it and return a matching sample value. The value only
 * selects the identifier KIND for the label walk; it is SHA-256 hashed before
 * traversal and never stored.
 */
function sampleIdentifierForAsset(node: NodeData, graph: AccessGraph | null): string {
  const cats = graph
    ? graph.edges
        .filter((e) => e.from === node.id)
        .map((e) => graph.nodes.find((n) => n.id === e.to)?.label ?? '')
        .join(' ')
        .toLowerCase()
    : '';
  if (cats.includes('aadhaar') || cats.includes('minor')) return '481527390475';
  if (cats.includes('pan')) return 'ABCDE1234F';
  if (cats.includes('phone')) return '+919876543210';
  return 'sample-subject@example.com';
}

/**
 * Build the Cytoscape elements from a live AccessGraph. Severity per
 * node is derived from the max severity of any edge touching the
 * node - so a clean asset stays uncolored, a critical edge paints
 * both endpoints critical.
 */
function buildLiveElements(graph: AccessGraph): ElementDefinition[] {
  const sevByNode: Record<string, Sev | undefined> = {};
  for (const e of graph.edges) {
    sevByNode[e.from] = maxSev(sevByNode[e.from], e.severity);
    sevByNode[e.to] = maxSev(sevByNode[e.to], e.severity);
  }
  const els: ElementDefinition[] = [];
  for (const n of graph.nodes) {
    const cat = catFromLive(n.kind, n.sourceType);
    const sev = sevByNode[n.id];
    els.push({
      data: { id: n.id, label: n.label, cat, sev, rows: n.count, kind: n.kind, sourceType: n.sourceType, countryCode: n.countryCode },
      classes: [`cat-${cat}`, sev ? `sev-${sev}` : 'sev-none'].join(' '),
    });
  }
  for (const e of graph.edges) {
    els.push({
      data: { id: `${e.from}->${e.to}->${e.findingId}`, source: e.from, target: e.to, sev: e.severity },
      // Production access-graph doesn't carry a "public bucket / cross-border"
      // risk tag on edges yet (Phase 6 will). Until then no risk-class is
      // applied; the visual still shows severity through node halos.
      classes: 'risk-normal',
    });
  }
  return els;
}

/**
 * Overlay the EGRESS half of the graph onto the access graph.
 *
 * The access graph answers "who can reach this data" and every node in it is
 * inside the estate. These are the arrows that point out: one destination node
 * per resolved country, and an edge per asset that data actually left from.
 *
 * `existingIds` matters. An asset can appear in both graphs - it is the same
 * uuid - and Cytoscape throws on a duplicate id. So assets already drawn by the
 * access graph are skipped here and only the edge is added, which is also the
 * behaviour you want visually: one node, two kinds of relationship.
 */
export function buildEgressElements(lin: EgressLineage, existingIds: Set<string>): ElementDefinition[] {
  const els: ElementDefinition[] = [];
  for (const n of lin.nodes) {
    if (existingIds.has(n.id)) continue;
    els.push({
      data: {
        id: n.id,
        label: n.kind === 'destination' ? countryLabel(n.countryCode ?? n.label) : n.label,
        cat: n.cat,
        sev: n.sev,
        rows: n.decisions,
        kind: n.kind,
        sourceType: n.sourceType,
        countryCode: n.countryCode,
        pii: n.pii,
      },
      classes: [`cat-${n.cat}`, n.sev ? `sev-${n.sev}` : 'sev-none'].join(' '),
    });
    existingIds.add(n.id);
  }
  const destCat = new Map(lin.nodes.map((n) => [n.id, n.cat]));
  for (const e of lin.edges) {
    els.push({
      data: {
        id: `egress:${e.source}->${e.target}`,
        source: e.source,
        target: e.target,
        sev: 'high',
        decisions: e.decisions,
        verdicts: e.verdicts.join(', '),
      },
      // Only a destination OUTSIDE the home jurisdiction gets the cross-border
      // treatment. An in-country egress edge is real movement but not a §16
      // event, and colouring it the same would train the eye to ignore both.
      classes: destCat.get(e.target) === 'cross-border' ? 'risk-cross-border' : 'risk-normal',
    });
  }
  return els;
}

/**
 * Render an ISO 3166-1 alpha-2 code as a country name where the browser can,
 * falling back to the raw code. `Intl.DisplayNames` is present in every browser
 * this app supports, but a bad code makes it throw rather than return the
 * input, so the call is guarded - an unrecognised code should show as itself,
 * not blank the node.
 */
export function countryLabel(cc: string): string {
  const code = (cc ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return code || 'unknown';
  try {
    // `fallback: 'code'` matters more than it looks. Without it an unassigned
    // but well-formed code resolves to the literal string "Unknown Region" -
    // not undefined - so a `?? code` guard never fires and the node renders a
    // destination nobody can look up. With it, the code comes back as itself.
    const dn = new Intl.DisplayNames(undefined, { type: 'region', fallback: 'code' });
    const name = dn.of(code);
    return !name || /^unknown region$/i.test(name) ? code : name;
  } catch {
    return code;
  }
}

export interface ExposurePathRow {
  id: string;
  from: string;
  to: string;
  severity: string;
  fromLabel: string;
  toLabel: string;
}

/**
 * Compute EVERY exposure path from the live graph (doc 33: the old hard
 * "top 3 highest-severity edges" cap is gone - hundreds of criticals must all
 * be reachable). A path is (source asset -> finding category), de-duplicated
 * and ranked by severity. The page searches / filters / virtualizes this list,
 * so it scales to thousands of edges without a cap.
 */
export function computeLiveExposurePaths(graph: AccessGraph): ExposurePathRow[] {
  const sevRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const sorted = [...graph.edges].sort((a, b) => (sevRank[b.severity] ?? 0) - (sevRank[a.severity] ?? 0));
  const out: ExposurePathRow[] = [];
  const seen = new Set<string>();
  // Build the id->label map once (O(N)) so labelling is O(1) per edge.
  const labelOf = new Map(graph.nodes.map((n) => [n.id, n.label] as const));
  const nodeLabel = (id: string) => labelOf.get(id) ?? id;
  for (const e of sorted) {
    const key = `${e.from}->${e.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id: e.findingId, from: e.from, to: e.to, severity: e.severity, fromLabel: nodeLabel(e.from), toLabel: nodeLabel(e.to) });
  }
  return out;
}

// Demo/preview data lives in `./Lineage.demo` and is only used when the
// URL carries `?demo=1`. The live graph is always preferred otherwise.
// (NODES/EDGES/EXPOSURE_PATHS literals previously inlined here violated
// Phase-2 §10 exit criterion 5 and showed up in the audit as KPI tiles
// computed from synthetic data presented as live tenant metrics.)
const DEMO_NODES_AS_NODEDATA: NodeData[] = DEMO_NODES;
const DEMO_EDGES_AS_EDGEDATA: Array<{ source: string; target: string; risk?: 'public' | 'cross-border' }> = DEMO_EDGES;

// SLICE 1 of the estate-map redesign (docs/design/DATA-ESTATE-MAP-REDESIGN.md).
//
// The defect this replaces: category AND severity were both encoded as HUE,
// across SEVEN different node shapes, at full Tailwind-500 saturation, as node
// FILLS. Two orthogonal variables competing for one channel is what produced
// the "traffic-light" look - and on a real estate, where most assets are
// low/medium, it floods the entire screen with colour.
//
// The reassignment:
//   category  -> SHAPE is gone entirely; category will be POSITION once the
//                estate regions land. One rounded rectangle for everything.
//   severity  -> a BORDER + rim, never the body fill.
//   volume    -> size (already the case).
//   selection -> brand orange, reserved and used for nothing else.
//
// Low and medium deliberately carry NO hue. Colour is for exceptions only; if
// the common case is coloured, nothing reads as exceptional. High and critical
// are additionally separated by rim WIDTH, because a colour-blindness
// simulation of this ramp showed amber and critical converging to a contrast
// ratio of 1.21 under deuteranopia - hue alone cannot carry severity for ~8% of
// male users.
const SEV_FILL: Record<Sev, { fill: string; rim: string; halo: string; rimWidth: number }> = {
  critical: { fill: '#111A2C', rim: '#E2484F', halo: '#E2484F', rimWidth: 3 },
  high:     { fill: '#111A2C', rim: '#E7A13C', halo: '#E7A13C', rimWidth: 2.5 },
  medium:   { fill: '#111A2C', rim: '#8A94A6', halo: '#22304A', rimWidth: 1.5 },
  low:      { fill: '#111A2C', rim: '#9AA3B2', halo: '#22304A', rimWidth: 1 },
};

// One shape. Category is carried by position and (later) an icon, never by a
// silhouette the viewer has to learn a legend for.
const CAT_FILL: Record<Cat, { fill: string; rim: string; halo: string; shape: cytoscape.Css.NodeShape }> = {
  app:           { fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
  pipeline:      { fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
  storage:       { fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
  analytics:     { fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
  'cross-border':{ fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
  export:        { fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
  db:            { fill: '#111A2C', rim: '#22304A', halo: '#22304A', shape: 'round-rectangle' },
};

// Pre-curated "exposure paths" type + risk-kind palette. The actual
// catalogue lives in `./Lineage.demo` and is only used in demo mode.
interface ExposurePath {
  id: string;
  title: string;
  hops: string[];
  riskKind: 'public' | 'cross-border' | 'minor';
  pii: string[];
  rationale: string;
  highlight: string[];
}

const RISK_KIND_META: Record<ExposurePath['riskKind'], { color: string; bg: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; label: string }> = {
  public:        { color: 'hsl(0, 84%, 60%)',  bg: 'from-red-500/30 to-red-500/5',     icon: Globe2,    label: 'Public exposure' },
  'cross-border':{ color: 'hsl(38, 92%, 50%)', bg: 'from-amber-500/30 to-amber-500/5', icon: Globe2,    label: 'Cross-border' },
  minor:         { color: 'hsl(271, 81%, 66%)',bg: 'from-purple-500/30 to-purple-500/5', icon: ShieldAlert, label: 'Section 9 (minor)' },
};

const metaHsl: Record<ExposurePath['riskKind'], string> = {
  public:        '0 84% 60%',
  'cross-border':'38 92% 50%',
  minor:         '271 81% 66%',
};

/**
 * Build Cytoscape elements from the demo catalogue. Live elements come
 * from `buildLiveElements(liveGraph)` defined above the literals.
 */
function buildDemoElements(): ElementDefinition[] {
  const els: ElementDefinition[] = [];
  for (const n of DEMO_NODES_AS_NODEDATA) {
    els.push({
      data: { ...n },
      classes: [`cat-${n.cat}`, n.sev ? `sev-${n.sev}` : 'sev-none'].join(' '),
    });
  }
  for (const e of DEMO_EDGES_AS_EDGEDATA) {
    els.push({
      data: { id: `${e.source}->${e.target}`, source: e.source, target: e.target },
      classes: e.risk ? `risk-${e.risk}` : 'risk-normal',
    });
  }
  return els;
}

export function DiscoveryLineage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [selected, setSelected] = useState<NodeData | null>(null);
  const [activePath, setActivePath] = useState<string | null>(null);
  const { theme } = useTheme();

  // Demo mode is opt-in via `?demo=1`. Default render is live data from
  // `GET /v1/discovery/access-graph`. The demo catalogue (DEMO_NODES,
  // DEMO_EDGES, DEMO_EXPOSURE_PATHS in ./Lineage.demo) is only used
  // when explicitly requested - keeps marketing screenshots possible
  // while preventing the prior bug where synthetic data appeared as
  // live tenant metrics.
  const demoMode = useMemo(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('demo'),
    [],
  );

  const [liveGraph, setLiveGraph] = useState<AccessGraph | null>(null);
  const [view, setView] = useState<'table' | 'estate' | 'graph'>('table');
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState<string | null>(null);
  const refreshLive = (signal?: AbortSignal) =>
    getAccessGraph({ depth: 2 })
      .then((r) => {
        if (signal?.aborted) return;
        setLiveGraph(r);
        setLiveError(null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        setLiveError(err instanceof Error ? err.message : 'load failed');
      })
      .finally(() => {
        if (!signal?.aborted) setLiveLoading(false);
      });
  // Egress lineage loads ALONGSIDE the access graph, not inside it. They answer
  // different questions ("who can reach it" vs "where did it go") and fail
  // independently: a tenant with no DDR gateway has no egress data at all, and
  // that must not blank the access graph it does have.
  const [egress, setEgress] = useState<EgressLineage | null>(null);
  const [egressError, setEgressError] = useState<string | null>(null);
  const refreshEgress = (signal?: AbortSignal) =>
    getEgressLineage({ days: 30 })
      .then((r) => {
        if (signal?.aborted) return;
        setEgress(r);
        setEgressError(null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        setEgressError(err instanceof Error ? err.message : 'load failed');
      });

  useEffect(() => {
    const ctrl = new AbortController();
    refreshLive(ctrl.signal);
    if (!demoMode) void refreshEgress(ctrl.signal);
    return () => ctrl.abort();
  }, [demoMode]);
  const liveStats = useMemo(() => liveGraph?.stats ?? null, [liveGraph]);

  /**
   * Real cross-border counts, replacing the severity proxy the tiles used
   * before this endpoint existed.
   *
   * `foreignDecisions` counts DECISIONS, not edges: one decision can touch
   * several findings on one asset, and counting edges would report movement
   * that did not happen.
   */
  const egressStats = useMemo(() => {
    if (!egress) return null;
    const foreign = egress.nodes.filter((n) => n.kind === 'destination' && n.cat === 'cross-border');
    return {
      countries: foreign.length,
      foreignDecisions: foreign.reduce((a, n) => a + (n.decisions ?? 0), 0),
      unresolved: egress.unresolvedDecisions,
      unattributed: egress.unattributedDecisions,
      considered: egress.coverage.decisionsConsidered,
      windowDays: egress.windowDays,
      homeCountry: egress.homeCountry,
      topCountries: [...foreign]
        .sort((a, b) => (b.decisions ?? 0) - (a.decisions ?? 0))
        .slice(0, 6)
        .map((n) => ({ code: n.countryCode ?? n.label, label: countryLabel(n.countryCode ?? n.label), decisions: n.decisions ?? 0 })),
    };
  }, [egress]);

  // KPI counts derived from the *active* dataset (live by default).
  const kpis = useMemo(() => {
    if (demoMode) {
      return {
        totalAssets: DEMO_NODES_AS_NODEDATA.length,
        piiAssets: DEMO_NODES_AS_NODEDATA.filter((n) => n.pii && n.pii.length > 0).length,
        crossBorderEdges: DEMO_EDGES_AS_EDGEDATA.filter((e) => e.risk === 'cross-border').length,
        publicEdges: DEMO_EDGES_AS_EDGEDATA.filter((e) => e.risk === 'public').length,
        minorAssets: DEMO_NODES_AS_NODEDATA.filter((n) => n.pii?.includes('minor_dob')).length,
      };
    }
    if (!liveGraph) {
      return { totalAssets: 0, piiAssets: 0, crossBorderEdges: 0, publicEdges: 0, minorAssets: 0 };
    }
    const assetNodes = liveGraph.nodes.filter((n) => n.kind === 'asset');
    const categoryEdges = liveGraph.edges.length;
    const sevRank = (s: AccessGraphEdge['severity']) =>
      s === 'critical' ? 4 : s === 'high' ? 3 : s === 'medium' ? 2 : 1;
    // "PII assets" = asset nodes that have at least one finding edge attached.
    const assetsWithFindings = new Set(
      liveGraph.edges.flatMap((e) => [e.from, e.to]).filter((id) => assetNodes.some((n) => n.id === id)),
    );
    return {
      totalAssets: assetNodes.length,
      piiAssets: assetsWithFindings.size,
      // Cross-border + public exposure flags aren't on AccessGraphEdge today
      // (Phase 6 will add them). Until then show finding-edge totals split
      // by severity buckets so the tiles still mean something.
      crossBorderEdges: liveGraph.edges.filter((e) => sevRank(e.severity) >= 3).length,
      publicEdges: liveGraph.edges.filter((e) => e.severity === 'critical').length,
      // Section 9 = finding categories whose label contains "minor".
      minorAssets: liveGraph.nodes.filter(
        (n) => n.kind === 'finding_category' && /minor/i.test(n.label),
      ).length,
      _categoryEdges: categoryEdges,
    };
  }, [demoMode, liveGraph]);

  // Top exposure paths: demo cards in demo mode; computed-from-live otherwise.
  const liveExposurePaths = useMemo(
    () => (demoMode || !liveGraph ? [] : computeLiveExposurePaths(liveGraph)),
    [demoMode, liveGraph],
  );

  // doc 33 - the exposure-path list is searchable + severity-filterable +
  // virtualized so EVERY path is reachable (no top-3 cap).
  const [pathSearch, setPathSearch] = useState('');
  const [pathSev, setPathSev] = useState<'all' | Sev>('all');
  const pathSevCounts = useMemo(() => {
    const c: Record<Sev, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const p of liveExposurePaths) if (p.severity in c) c[p.severity as Sev] += 1;
    return c;
  }, [liveExposurePaths]);
  const filteredPaths = useMemo(() => {
    const q = pathSearch.trim().toLowerCase();
    return liveExposurePaths.filter(
      (p) =>
        (pathSev === 'all' || p.severity === pathSev) &&
        (!q || `${p.fromLabel} ${p.toLabel} ${p.id}`.toLowerCase().includes(q)),
    );
  }, [liveExposurePaths, pathSearch, pathSev]);

  useEffect(() => {
    if (!containerRef.current) return;
    const isDark = theme === 'dark';
    const labelColor = isDark ? '#f1f5f9' : '#0f172a';
    const labelOutline = isDark ? '#0a0e1a' : '#ffffff';
    const edgeColor = isDark ? '#475569' : '#94a3b8';

    // Category styles with radial-gradient fills (neumorphic / 3D effect)
    const catStyles: cytoscape.StylesheetStyle[] = (Object.keys(CAT_FILL) as Cat[]).map((cat) => {
      const c = CAT_FILL[cat];
      return {
        selector: `node.cat-${cat}`,
        style: ({
          // Category no longer paints anything. It sets the shape - now ONE rounded
          // rectangle for every category - and nothing else. The severity rules load
          // after this and own colour. Category will be carried by POSITION once the
          // estate regions land, not by a silhouette needing a legend.
          'background-color': isDark ? '#111A2C' : '#FFFFFF',
          'background-opacity': 1,
          'shape': c.shape,
        } as unknown) as cytoscape.Css.Node,
      };
    });

    // Severity styles with strong glow halos
    const sevStyles: cytoscape.StylesheetStyle[] = (Object.keys(SEV_FILL) as Sev[]).map((sev) => {
      const c = SEV_FILL[sev];
      return {
        selector: `node.sev-${sev}`,
        style: ({
          // FLAT body, and THEME-AWARE. Hardcoding the dark surface here turned
          // every node into a black block on white in light mode - worse than
          // the palette it replaced. The severity rims are mid-tone and stay
          // legible on both grounds, so only the surface flips.
          'background-color': isDark ? '#111A2C' : '#FFFFFF',
          'background-opacity': 1,
          // Severity lives HERE - colour and width together, so the two highest
          // levels stay distinguishable when hue is unavailable.
          'border-color': c.rim,
          'border-width': c.rimWidth,
          'border-opacity': 1,
          // Glow reserved for the two levels that are genuinely exceptional,
          // and at a fraction of the old opacity (was up to 0.95, which on a
          // full estate turns the canvas into a smear and flattens the very
          // ranking it means to express).
          'shadow-blur': sev === 'critical' ? 16 : sev === 'high' ? 10 : 0,
          'shadow-color': c.halo,
          'shadow-opacity': sev === 'critical' ? 0.35 : sev === 'high' ? 0.22 : 0,
          'shadow-offset-x': 0,
          'shadow-offset-y': 0,
        } as unknown) as cytoscape.Css.Node,
      };
    });

    // Build the active element set:
    //   • demo mode → the rich hand-curated DEMO catalogue
    //   • live mode + liveGraph loaded → adapter `buildLiveElements`
    //   • live mode + still loading or errored → empty graph until ready
    const elements: ElementDefinition[] = demoMode
      ? buildDemoElements()
      : liveGraph
        ? buildLiveElements(liveGraph)
        : [];

    // Overlay the outbound half. Additive on purpose: egress arrives on its own
    // schedule and may be absent entirely (no DDR gateway, or nothing enforced
    // in the window), and neither case should hold up or blank the access graph.
    if (!demoMode && egress) {
      elements.push(...buildEgressElements(egress, new Set(elements.map((el) => String(el.data.id)))));
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node',
          style: {
            'label': 'data(label)',
            'font-size': '11px',
            'font-weight': 600,
            'font-family': 'Inter, system-ui, sans-serif',
            'color': labelColor,
            'text-outline-color': labelOutline,
            'text-outline-width': 3,
            'text-valign': 'bottom',
            'text-margin-y': 10,
            'width': 42,
            'height': 42,
            'border-width': 2,
            'transition-property': 'background-color, border-color, width, height, opacity, border-width, shadow-blur',
            'transition-duration': 250,
          } as cytoscape.Css.Node,
        },
        ...catStyles,
        ...sevStyles,
        {
          selector: 'node.sev-critical',
          style: { 'width': 52, 'height': 52, 'border-width': 4 },
        },
        {
          selector: 'node:selected',
          style: {
            'border-color': '#06b6d4',
            'border-width': 5,
            'width': 60,
            'height': 60,
            'shadow-blur': 24,
            'shadow-color': '#F15D27',
            'shadow-opacity': 0.8,
          } as cytoscape.Css.Node,
        },
        {
          selector: 'node.path-glow',
          style: {
            'border-color': '#06b6d4',
            'border-width': 4,
            'shadow-blur': 30,
            'shadow-color': '#22d3ee',
            'shadow-opacity': 0.85,
            'z-index': 999,
          } as cytoscape.Css.Node,
        },
        {
          selector: 'edge',
          style: {
            'curve-style': 'bezier',
            'target-arrow-shape': 'triangle',
            'target-arrow-color': edgeColor,
            'line-color': edgeColor,
            'width': 2,
            'arrow-scale': 1.4,
            'opacity': 0.85,
          },
        },
        {
          selector: 'edge.path-glow',
          style: {
            'line-color': '#06b6d4',
            'target-arrow-color': '#06b6d4',
            'width': 4,
            'opacity': 1,
            'shadow-color': '#22d3ee',
            'shadow-blur': 14,
            'shadow-opacity': 0.6,
            'shadow-offset-x': 0,
            'shadow-offset-y': 0,
            'z-index': 998,
          } as cytoscape.Css.Edge,
        },
        {
          selector: 'edge.risk-public',
          style: {
            'line-color': '#ef4444',
            'target-arrow-color': '#ef4444',
            'width': 4,
            'line-style': 'dashed',
            'line-dash-pattern': [10, 5],
            'opacity': 1,
            'shadow-color': '#ef4444',
            'shadow-blur': 14,
            'shadow-opacity': 0.7,
            'shadow-offset-x': 0,
            'shadow-offset-y': 0,
            'label': 'PUBLIC EXPOSURE',
            'font-size': '10px',
            'font-weight': 700,
            'color': '#ef4444',
            'text-outline-color': labelOutline,
            'text-outline-width': 3,
            'text-rotation': 'autorotate',
            'text-margin-y': -10,
          } as cytoscape.Css.Edge,
        },
        {
          selector: 'edge.risk-cross-border',
          style: {
            'line-color': '#f59e0b',
            'target-arrow-color': '#f59e0b',
            'width': 3.5,
            'opacity': 1,
            'shadow-color': '#f59e0b',
            'shadow-blur': 12,
            'shadow-opacity': 0.6,
            'shadow-offset-x': 0,
            'shadow-offset-y': 0,
            'label': 'CROSS-BORDER',
            'font-size': '10px',
            'font-weight': 700,
            'color': '#f59e0b',
            'text-outline-color': labelOutline,
            'text-outline-width': 3,
            'text-rotation': 'autorotate',
            'text-margin-y': -10,
          } as cytoscape.Css.Edge,
        },
        { selector: '.faded', style: { 'opacity': 0.15 } as cytoscape.Css.Node },
        { selector: '.highlight', style: { 'opacity': 1, 'z-index': 999 } as cytoscape.Css.Node },
      ],
      layout: {
        name: 'dagre',
        rankDir: 'LR',
        // Tighter vertical packing + wider rank gap reads far better for the
        // bipartite asset->category shape than the old 70/130 (which produced a
        // tall, hard-to-scan column). Grid alignment keeps rows legible.
        nodeSep: 26,
        rankSep: 220,
        edgeSep: 12,
        ranker: 'tight-tree',
        padding: 40,
        animate: true,
        animationDuration: 500,
      } as cytoscape.LayoutOptions,
      // Wheel zoom, tuned by hand against the actual complaint rather than by
      // theory. 0.2 -> 0.5 -> 1 were each still slow (15-20 rolls to reach a
      // readable scale); 2 gets there in a few. Cytoscape logs a warning for
      // any value other than 1 - noted and accepted, because 1 measurably was
      // not enough on this canvas.
      wheelSensitivity: 2,
      minZoom: 0.15,
      maxZoom: 3,
    });

    cy.on('tap', 'node', (evt) => {
      const node = evt.target;
      cy.elements().addClass('faded').removeClass('highlight');
      const nbh = node.closedNeighborhood();
      nbh.removeClass('faded').addClass('highlight');
      setSelected(node.data() as NodeData);
    });

    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        cy.elements().removeClass('faded').removeClass('highlight').removeClass('path-glow');
        setSelected(null);
        setActivePath(null);
      }
    });

    // Marching-ants on PUBLIC EXPOSURE edges
    let dashOffset = 0;
    // WCAG 2.2.2 (Pause, Stop, Hide) is LEVEL A, and it is the tier enterprise
    // procurement checks in a VPAT. Content that moves automatically for more
    // than five seconds needs a pause control. This ran forever, on every
    // public-exposure edge, with no control at all.
    //
    // It also violated "never animate the same thing twice" - those edges
    // already carry a distinct colour, width, glow and a PUBLIC EXPOSURE label.
    // Marching ants natively signal SELECTION, which is the right semantic, so
    // it now runs only while an edge is actually selected, and honours the
    // user's reduced-motion preference.
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    const animationInterval = reduceMotion
      ? null
      : setInterval(() => {
          const marching = cy.edges('.risk-public:selected');
          if (marching.length === 0) return; // nothing selected - stay still
          dashOffset = (dashOffset - 1) % 15;
          marching.style('line-dash-offset', dashOffset);
        }, 60);

    cyRef.current = cy;

    // doc 33 - "jammed in a corner" fix. Inside the federated remote the graph
    // container often mounts at 0x0 then gets its real size a frame later, so a
    // layout that ran against a 0-size viewport leaves every node piled in the
    // top-left. Re-fit after layout settles, once on the next frame, and on any
    // container resize so the graph always fills and centres.
    const fitToView = (): void => {
      if (cy.destroyed?.() || cy.nodes().length === 0) return;
      cy.resize();
      cy.fit(undefined, 40);
      cy.center();
    };
    cy.one('layoutstop', fitToView);
    const rafId = requestAnimationFrame(fitToView);

    let resizeRaf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(fitToView);
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      if (animationInterval) clearInterval(animationInterval);
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(resizeRaf);
      ro.disconnect();
      cy.destroy();
      cyRef.current = null;
    };
    // Re-mount Cytoscape when theme OR either dataset changes. `egress` is in
    // here because it resolves on its own schedule: without it the destination
    // nodes are built once against a null and never repaint when the call
    // lands, which looks exactly like an estate with no egress.
  }, [theme, demoMode, liveGraph, egress]);

  // Demo-mode path focus (hand-curated node-id list).
  function focusDemoPath(path: ExposurePath) {
    const cy = cyRef.current;
    if (!cy) return;
    setActivePath(path.id);
    cy.elements().removeClass('highlight').removeClass('path-glow').addClass('faded');
    const nodeIds = new Set(path.highlight);
    const pathNodes = cy.nodes().filter((n) => nodeIds.has(n.id()));
    pathNodes.removeClass('faded').addClass('path-glow');
    for (let i = 0; i < path.highlight.length - 1; i++) {
      const a = path.highlight[i];
      const b = path.highlight[i + 1];
      cy.edges(`[source = "${a}"][target = "${b}"]`).removeClass('faded').addClass('path-glow');
    }
    cy.fit(pathNodes, 80);
  }

  // Live-mode path focus: zoom on a single source node + its outgoing edges.
  function focusLivePath(sourceNodeId: string) {
    const cy = cyRef.current;
    if (!cy) return;
    setActivePath(sourceNodeId);
    cy.elements().removeClass('highlight').removeClass('path-glow').addClass('faded');
    const focus = cy.nodes(`#${CSS.escape(sourceNodeId)}`);
    if (focus.empty()) return;
    const nbh = focus.closedNeighborhood();
    nbh.removeClass('faded').addClass('path-glow');
    cy.fit(focus, 120);
  }

  return (
    <PageContainer className="glass-ambient">
      <PageHeader
        title="Data Lineage"
        subtitle="Column-graph sibling-walk lineage. Every node is an asset; every edge is a flow. PII risk propagates downstream - visualize it before the auditor does."
        badges={[
          { label: 'Column-graph', variant: 'glass' },
          { label: 'PII Flow', variant: 'info' },
          ...(demoMode
            ? [{ label: 'Demo preview', variant: 'warning' as const }]
            : liveLoading
              ? [{ label: 'Loading live graph', variant: 'glass' as const }]
              : liveError
                ? [{ label: 'Live graph unavailable', variant: 'warning' as const }]
                : liveStats
                  ? [{ label: `${liveStats.assetCount} live nodes`, variant: 'success' as const }]
                  : []),
        ]}
        actions={
          <>
            <Button variant="glass" size="sm" onClick={() => cyRef.current?.zoom(cyRef.current.zoom() * 1.25)} title="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
            <Button variant="glass" size="sm" onClick={() => cyRef.current?.zoom(cyRef.current.zoom() * 0.8)} title="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
            <Button variant="glass" size="sm" onClick={() => cyRef.current?.fit(undefined, 30)} title="Fit"><Maximize className="h-4 w-4" /></Button>
            <Button variant="glass" size="sm" onClick={() => cyRef.current?.layout({ name: 'dagre', rankDir: 'LR' } as cytoscape.LayoutOptions).run()} title="Re-layout"><RotateCcw className="h-4 w-4" /></Button>
          </>
        }
      />

      {/* Prominent demo-mode banner - only when the URL flag is set. */}
      {demoMode && (
        <div className="glass-lite border-l-4 border-l-warning rounded-md p-3 mb-4 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-foreground">Demo preview - synthetic catalogue</div>
            <div className="text-muted-foreground mt-0.5">
              You loaded this page with <code>?demo=1</code>. The graph, KPIs and exposure paths shown here come from a hand-curated 17-node example (<code>Lineage.demo.ts</code>) - not your live tenant. To see live data, remove the <code>?demo=1</code> query param.
            </div>
          </div>
        </div>
      )}

      <PageGuide id="lineage">
        This shows how your sensitive data moves between systems - each line is data flowing from one
        place to another, and red lines are the riskiest. It helps you spot where sensitive data could
        leak before an auditor does. Click any path to focus the map on it.
      </PageGuide>

      {/* KPI strip - values come from whichever dataset is active (live by default). */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <NeoKpi icon={Layers3}    label="Total assets"        value={kpis.totalAssets}      hint={demoMode ? 'demo: apps · DBs · pipelines · sinks' : 'live asset nodes in your access graph'} tone="info" />
        <NeoKpi icon={Database}   label="PII assets"          value={kpis.piiAssets}        hint={demoMode ? 'demo: contain Aadhaar / PAN / phone / email / DOB' : 'live asset nodes with at least one finding edge'} tone="warn" />
        {/* Real cross-border once the egress endpoint answers. Before it
            existed this tile showed a severity PROXY and its hint said so.
            It now counts decisions that actually resolved to a foreign
            jurisdiction, and falls back to the proxy only when egress is
            unavailable - a tenant with no gateway still gets a truthful tile. */}
        {!demoMode && egressStats ? (
          <NeoKpi
            icon={Globe2}
            label="Cross-border"
            value={egressStats.foreignDecisions}
            hint={`DPDPA §16 · ${egressStats.countries} ${egressStats.countries === 1 ? 'country' : 'countries'} outside ${egressStats.homeCountry} · last ${egressStats.windowDays}d`}
            tone="critical"
          />
        ) : (
          <NeoKpi icon={Globe2}     label="High-sev edges"      value={kpis.crossBorderEdges} hint={demoMode ? 'demo: DPDPA §16 transfer review' : 'live edges with severity ≥ high'} tone="warn" />
        )}
        <NeoKpi icon={FileWarning} label="Critical edges"     value={kpis.publicEdges}      hint={demoMode ? 'demo: data leaving via public bucket' : 'live edges with severity = critical'} tone="critical" />
        <NeoKpi icon={ShieldAlert} label="Section 9 (minor)"  value={kpis.minorAssets}      hint="DPDPA §9 - parental consent required" tone="critical" />
      </div>

      {/* Live lineage summary from the backend (always shown in live mode). */}
      {!demoMode && !liveLoading && !liveError && liveStats && (
        <div className="glass-lite rounded-md p-3 mb-4 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
            <span className="font-medium">Live lineage from this tenant</span>
          </div>
          <span className="text-muted-foreground">
            {liveStats.assetCount} assets · {liveStats.categoryCount} finding categories ·
            {' '}{liveStats.edgeCount} edges
          </span>
          <span className="text-[10px] text-muted-foreground ml-auto">
            Source: <code>GET /v1/discovery/access-graph?depth={liveStats.depthRequested}</code>
          </span>
        </div>
      )}
      {/* Egress summary. Shown whenever the endpoint answered, INCLUDING when
          it found nothing - an absent panel and an empty estate look identical,
          and only one of them is good news. The unresolved count is on the same
          line as the destinations for the same reason: a jurisdiction list
          without it reads as complete. */}
      {!demoMode && egressStats && (
        <div className="glass-lite rounded-md p-3 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <div className="flex items-center gap-2">
            <Globe2 className="h-3.5 w-3.5 text-sev-critical" />
            <span className="font-medium">Left the estate</span>
          </div>
          {egressStats.topCountries.length > 0 ? (
            <span className="flex flex-wrap items-center gap-1.5">
              {egressStats.topCountries.map((c) => (
                <span
                  key={c.code}
                  className="rounded-sm border border-sev-critical/40 bg-sev-critical/10 px-1.5 py-0.5 tabular-nums"
                  title={`${c.decisions} enforced ${c.decisions === 1 ? 'decision' : 'decisions'} resolved to ${c.label}`}
                >
                  {c.label} <span className="text-muted-foreground">{c.decisions}</span>
                </span>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">
              No enforced decision resolved outside {egressStats.homeCountry} in the last {egressStats.windowDays} days.
            </span>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-3">
            {egressStats.unresolved > 0 && (
              <span
                className="text-warning"
                title="Enforced decisions whose source could not be resolved to a country. Real egress with no place on the map - counted rather than dropped."
              >
                {egressStats.unresolved} unresolved
              </span>
            )}
            {egressStats.unattributed > 0 && (
              <span title="Decisions with a country but no linked finding, so there is no asset to draw the arrow from.">
                {egressStats.unattributed} unattributed
              </span>
            )}
            <span title="decide.ts persists only non-allow decisions by design, so this graph is ENFORCED egress, not all egress.">
              enforced only
            </span>
          </span>
        </div>
      )}
      {!demoMode && egressError && (
        <div className="glass-lite rounded-md p-3 mb-4 text-xs text-warning">
          Egress lineage unavailable ({egressError}). The access graph below is unaffected - but it
          shows who can REACH data, not where data went, so do not read it as an all-clear.
        </div>
      )}
      {!demoMode && liveError && (
        <ErrorState
          title="Live lineage unavailable"
          detail={liveError}
          onRetry={() => { setLiveLoading(true); setLiveError(null); refreshLive(); }}
        />
      )}
      {!demoMode && !liveLoading && !liveError && liveGraph && liveGraph.nodes.length === 0 && (
        <div className="glass-lite rounded-md p-6 text-center text-xs text-muted-foreground mb-4">
          No lineage edges discovered yet for this tenant. Plant a connector or run an asset scan to populate the graph.
        </div>
      )}

      {/* Exposure paths. Demo mode: the curated 3-card narrative. Live mode:
          EVERY exposure path, searchable + severity-filterable + virtualized
          (doc 33 - the old hard top-3 cap is gone). */}
      <div className="mb-4">
        <div className="flex items-end justify-between mb-2">
          <div>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
              {demoMode ? 'Top 3 exposure paths (demo)' : `Exposure paths (live${liveExposurePaths.length ? ` · ${liveExposurePaths.length}` : ''})`}
            </h2>
            <p className="text-sm text-foreground mt-0.5">
              {demoMode
                ? 'Click a card to focus the graph on that path. The path lights up in cyan.'
                : 'Every path from this tenant’s live access graph, ranked by severity. Search or filter, then click any row to focus the graph on that source asset.'}
            </p>
          </div>
        </div>
        {demoMode ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {DEMO_EXPOSURE_PATHS.map((p, i) => (
              <ExposurePathCard
                key={p.id}
                path={p}
                active={activePath === p.id}
                onClick={() => focusDemoPath(p)}
                delay={i * 0.05}
              />
            ))}
          </div>
        ) : liveExposurePaths.length > 0 ? (
          <GlassCard className="p-0 overflow-hidden">
            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border/40">
              <div className="glass-lite flex items-center gap-2 rounded-md px-2.5 py-1 flex-1 min-w-[200px] max-w-sm">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  value={pathSearch}
                  onChange={(e) => setPathSearch(e.target.value)}
                  placeholder="Search asset or finding..."
                  className="flex-1 bg-transparent outline-none text-xs placeholder:text-muted-foreground/70"
                />
              </div>
              <div className="flex items-center gap-1 text-xs">
                {(['all', 'critical', 'high', 'medium', 'low'] as const).map((s) => {
                  const n = s === 'all' ? liveExposurePaths.length : pathSevCounts[s];
                  return (
                    <button
                      key={s}
                      onClick={() => setPathSev(s)}
                      className={cn(
                        'rounded-full px-2.5 py-0.5 capitalize transition-colors border',
                        pathSev === s ? 'border-primary/50 bg-primary/15 text-primary' : 'border-transparent hover:bg-muted/40 text-muted-foreground',
                      )}
                    >
                      {s} ({n})
                    </button>
                  );
                })}
              </div>
            </div>
            {filteredPaths.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No paths match. Clear the search or severity filter.
              </div>
            ) : (
              <VirtualList<ExposurePathRow>
                items={filteredPaths}
                height={Math.min(320, Math.max(56, filteredPaths.length * 52))}
                itemHeight={52}
                getKey={(p, i) => `${p.from}->${p.to}:${i}`}
                renderRow={(p) => (
                  <ExposurePathRowView
                    path={p}
                    active={activePath === p.from}
                    onClick={() => focusLivePath(p.from)}
                  />
                )}
              />
            )}
          </GlassCard>
        ) : (
          <div className="glass-lite rounded-md p-4 text-center text-xs text-muted-foreground">
            No finding edges in your access graph yet. As connectors discover PII, exposure paths will appear here.
          </div>
        )}
      </div>

      {/* What you're looking at - explainer */}
      <GlassCard className="p-4 mb-4 border-l-4 border-l-info">
        <div className="flex items-start gap-3">
          <Info className="h-4 w-4 text-info shrink-0 mt-0.5" />
          <div className="space-y-1.5 text-xs text-muted-foreground leading-relaxed">
            <p className="text-foreground font-medium">How to read this graph</p>
            <p>
              Every node is a <strong className="text-foreground">data asset</strong> (app · OLTP · ETL · lake · DWH · BI · external destination); shape encodes the asset category, color encodes severity.
              The <strong style={{ color: '#ef4444' }}>red dashed marching-ants</strong> edge marks a public-bucket exposure;
              the <strong style={{ color: '#f59e0b' }}>orange</strong> edge is a cross-border transfer (SCC + TIA required).
              Glowing red nodes hold <strong style={{ color: '#ef4444' }}>critical PII</strong> (Aadhaar / Section 9 minor data).
              Click any node for the inspector; click an exposure-path card above to highlight the entire path.
            </p>
          </div>
        </div>
      </GlassCard>

      <div className="flex items-center gap-1 mb-3">
        {(['table', 'estate', 'graph'] as const).map((v) => (
          <button key={v} type="button" onClick={() => setView(v)}
            className={`px-3 py-1.5 rounded-md text-xs ${view === v ? 'bg-primary/15 border border-primary/40' : 'text-muted-foreground border border-transparent'}`}>
            {v === 'table' ? 'Table' : v === 'estate' ? 'Estate map' : 'Connection graph'}
          </button>
        ))}
      </div>

      {(view === 'estate' || view === 'table') && (
        <GlassCard className="p-4 mb-4 glass-3d-frame">
          <EstateMap
            mode={view === 'table' ? 'table' : 'map'}
            graph={liveGraph}
            onPick={(assetId) => {
              // Reuse the exposure-path focus: highlight the asset and its
              // neighbourhood, fade everything else, fit to it. Then bring the
              // graph into view, because the answer is useless if it happens
              // off-screen.
              focusLivePath(assetId);
              containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
          />
        </GlassCard>
      )}


      {/* Graph + Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <GlassCard className="lg:col-span-3 p-0 overflow-hidden h-[640px] relative glass-3d-frame">
          <div ref={containerRef} className="w-full h-full" />
          <Legend />
          <div className="absolute top-3 right-3 glass-strong px-3 py-1.5 rounded-md text-[10px] text-muted-foreground flex items-center gap-1.5 pointer-events-none">
            <Activity className="h-3 w-3 text-info" />
            <span>
              {demoMode
                ? `Demo · ${DEMO_NODES_AS_NODEDATA.length} nodes · ${DEMO_EDGES_AS_EDGEDATA.length} edges`
                : liveGraph
                  ? `Live · ${liveGraph.nodes.length} nodes · ${liveGraph.edges.length} edges`
                  : liveLoading
                    ? 'Loading…'
                    : 'No data'}
            </span>
          </div>
        </GlassCard>

        <GlassCard className="p-5 h-[640px] overflow-y-auto glass-3d-frame">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="text-base">{selected ? selected.label : 'Inspector'}</CardTitle>
            <CardDescription>{selected ? selected.cat.toUpperCase().replace('-', ' ') : 'Click any node to inspect'}</CardDescription>
          </CardHeader>

          {selected ? (
            <motion.div key={selected.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 text-sm">
              {/* Neumorphic asset preview tile */}
              <div className="glass-3d p-4 flex items-center gap-3">
                <NodePreview cat={selected.cat} sev={selected.sev} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Asset</div>
                  <div className="text-sm font-semibold truncate">{selected.label}</div>
                  <div className="text-[10px] text-muted-foreground capitalize mt-0.5">{selected.cat.replace('-', ' ')}</div>
                </div>
              </div>

              {selected.sev && (
                <div className="glass-lite p-3 rounded-md flex items-center gap-2.5">
                  <ShieldAlert className={`h-4 w-4 ${selected.sev === 'critical' ? 'animate-pulse' : ''}`} style={{ color: SEV_FILL[selected.sev].fill }} />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Risk classification</div>
                    <Badge variant={selected.sev as 'critical' | 'high' | 'medium' | 'low'}>{selected.sev}</Badge>
                  </div>
                </div>
              )}
              {selected.sourceType && (() => {
                const loc = deriveLocation(selected.sourceType, selected.label, selected.countryCode);
                const exposures = liveGraph
                  ? Array.from(new Set(
                      liveGraph.edges
                        .filter((e) => e.from === selected.id)
                        .map((e) => liveGraph.nodes.find((n) => n.id === e.to)?.label)
                        .filter((l): l is string => !!l),
                    ))
                  : [];
                return (
                  <div className="glass-lite p-3 rounded-md space-y-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Where this data lives
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold text-[10px]"
                        style={{ background: `${loc.accent}22`, color: loc.accent, border: `1px solid ${loc.accent}55` }}
                      >
                        <Cloud className="h-3 w-3" /> {loc.provider}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{loc.service}</span>
                    </div>
                    {(loc.region || loc.regionLabel) && (
                      <div className="flex items-center gap-1 text-[11px] text-foreground/90">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        {loc.region ?? ''}{loc.regionLabel ? ` · ${loc.regionLabel}` : ''}
                      </div>
                    )}
                    <div className="space-y-1 text-[10px]">
                      {loc.container && (
                        <div className="flex gap-1.5">
                          <span className="text-muted-foreground/70 w-16 shrink-0">{loc.containerKind}</span>
                          <code className="font-mono text-foreground/90 break-all">{loc.container}</code>
                        </div>
                      )}
                      {loc.path && (
                        <div className="flex gap-1.5">
                          <span className="text-muted-foreground/70 w-16 shrink-0">{loc.pathKind}</span>
                          <code className="font-mono text-foreground/90 break-all">{loc.path}</code>
                        </div>
                      )}
                    </div>
                    <div className="flex items-start gap-1 text-[10px] text-muted-foreground/80 pt-1 border-t border-border/20">
                      <Server className="h-3 w-3 mt-0.5 shrink-0" />
                      <span className="break-all">{loc.reach}</span>
                    </div>
                    {exposures.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Exposes</div>
                        <div className="flex flex-wrap gap-1">
                          {exposures.map((e) => <Badge key={e} variant="glass" className="text-[9px]">{e}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {selected.pii && selected.pii.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">PII categories</div>
                  <div className="flex flex-wrap gap-1">
                    {selected.pii.map((p) => <Badge key={p} variant="glass" className="text-[10px]">{p}</Badge>)}
                  </div>
                </div>
              )}
              {selected.rows != null && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rows</div>
                  <div className="text-2xl font-display tabular">{selected.rows.toLocaleString('en-IN')}</div>
                </div>
              )}
              <div className="pt-3 border-t border-border/30 space-y-2">
                <Button
                  size="sm"
                  variant="glass"
                  className="w-full"
                  onClick={() => {
                    const seed = sampleIdentifierForAsset(selected, liveGraph);
                    try { sessionStorage.setItem('dsar_seed', seed); } catch { /* ignore */ }
                    navigate({ to: '/dspm/reverse-dsar', search: { identifier: seed } as never });
                  }}
                  title="Find everywhere a data subject in this asset appears across the estate - runs immediately"
                >
                  <GitBranch className="h-3 w-3" /> Find a person's data from here
                </Button>
                <Button
                  size="sm"
                  variant="glass"
                  className="w-full"
                  onClick={() => navigate({ to: '/dspm/findings', search: { asset: selected.label } as never })}
                  title="Open Findings filtered to this asset"
                >
                  <AlertTriangle className="h-3 w-3" /> Open findings on this asset
                </Button>
                <Button
                  size="sm"
                  variant="glass"
                  className="w-full"
                  onClick={() => navigate({ to: '/detection/rules', search: { asset: selected.label } as never })}
                  title="Author or activate a masking rule for this asset in Detection / Rules"
                >
                  <Lock className="h-3 w-3" /> Apply masking policy
                </Button>
              </div>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground text-center">
              <Database className="h-12 w-12 opacity-30" />
              <p className="text-xs max-w-[200px]">Click any node to see its PII categories, severity, row count, and downstream impact. Or click an exposure-path card to focus the graph.</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Below-graph interpretation panel. ILLUSTRATIVE - the three cards
          below are curated examples that match the DEMO graph (rds.customers,
          bigquery.eu-west, rds.kyc). They are NOT derived from live tenant
          data, so they are gated to demo mode (`?demo=1`) exactly like
          DEMO_NODES/EDGES/PATHS. Showing them in live mode presented
          fabricated auditor findings as if real (caught 2026-06-05). A
          live-data version (computed from the access-graph) is the follow-up. */}
      {demoMode && (
        <GlassCard className="p-5 mt-4">
          <div className="flex items-start gap-3 mb-3">
            <Sparkles className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-display font-semibold">What this lineage tells the auditor <span className="text-[10px] uppercase tracking-wide text-muted-foreground">· example</span></h3>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">Three things a DPO / auditor will look for &mdash; surfaced in plain language, derived from the demo graph above.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <InterpretCard
              title="Single-hop PII reach"
              value="3 hops"
              detail="rds.customers (Aadhaar) is exactly 3 hops from a public bucket via Airflow → dbt → snowflake. Detection rule R-IN-014 blocks this path at runtime."
              tone="critical"
            />
            <InterpretCard
              title="Cross-border drift"
              value="234,000 rows"
              detail="Email PII flows to bigquery.eu-west.users without an SCC + TIA on file. DPDPA §16 reviewer must approve before next sync."
              tone="warn"
            />
            <InterpretCard
              title="Section 9 (minor)"
              value="832 rows"
              detail="rds.kyc carries minor DOB. Verifiable parental consent required per DPDPA §9. Detection blocks reads of minor_dob unless purpose=parental_consent_verified."
              tone="info"
            />
          </div>
        </GlassCard>
      )}
    </PageContainer>
  );
}

// ─────────────────────────────────────────────────
// KPI card with neumorphic depth
// ─────────────────────────────────────────────────
interface NeoKpiProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: number | string;
  hint: string;
  tone: 'info' | 'warn' | 'critical' | 'success';
}

function NeoKpi({ icon: Icon, label, value, hint, tone }: NeoKpiProps) {
  const toneHsl =
    tone === 'critical' ? '0 84% 60%' :
    tone === 'warn'     ? '21 94% 55%' :
    tone === 'info'     ? '217 91% 60%' :
    '142 71% 45%';
  const toneColor =
    tone === 'critical' ? 'hsl(0, 84%, 60%)' :
    tone === 'warn'     ? 'hsl(21, 94%, 55%)' :
    tone === 'info'     ? 'hsl(217, 91%, 60%)' :
    'hsl(142, 71%, 45%)';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="glass-3d p-4 h-full">
        <div className="flex items-start justify-between mb-2.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
          <div
            className="icon-glass h-8 w-8 grid place-items-center shrink-0"
            style={{ '--tone': toneHsl } as React.CSSProperties}
          >
            <Icon className="h-3.5 w-3.5" style={{ color: toneColor }} />
          </div>
        </div>
        <div className="text-3xl font-display tabular leading-tight">{value}</div>
        <div className="text-[10px] text-muted-foreground leading-snug mt-1.5">{hint}</div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────
// Exposure path hero card with isometric/3D feel
// ─────────────────────────────────────────────────
interface ExposurePathCardProps {
  path: ExposurePath;
  active: boolean;
  onClick: () => void;
  delay: number;
}

function ExposurePathCard({ path, active, onClick, delay }: ExposurePathCardProps) {
  const meta = RISK_KIND_META[path.riskKind];
  const Icon = meta.icon;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
      whileTap={{ scale: 0.99 }}
      className={`group relative text-left rounded-xl glass-3d glass-3d-accent w-full ${active ? 'ring-1 ring-info' : ''}`}
      style={{ '--accent': meta.color } as React.CSSProperties}
    >
      {/* Aurora background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${meta.bg} opacity-50 pointer-events-none`} />
      <div
        className="absolute -top-8 -right-8 h-24 w-24 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${meta.color}40 0%, transparent 70%)` }}
      />

      <div className="relative p-4">
        <div className="flex items-center justify-between mb-2">
          <div
            className="icon-glass h-9 w-9 grid place-items-center shrink-0"
            style={{ '--tone': metaHsl[path.riskKind] } as React.CSSProperties}
          >
            <Icon className="h-4 w-4" style={{ color: meta.color }} />
          </div>
          <Badge variant="glass" className="text-[9px] uppercase tracking-wider">{meta.label}</Badge>
        </div>
        <h3 className="text-sm font-display font-semibold mb-2 leading-tight">{path.title}</h3>

        {/* Hop pipeline */}
        <div className="flex flex-wrap items-center gap-1 mb-2">
          {path.hops.map((h, i) => (
            <span key={`${h}-${i}`} className="flex items-center gap-1 text-[10px]">
              <span className="px-1.5 py-0.5 rounded glass-lite truncate max-w-[100px]">{h}</span>
              {i < path.hops.length - 1 && <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />}
            </span>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground leading-snug mb-2">{path.rationale}</p>

        <div className="flex flex-wrap gap-1 mb-2">
          {path.pii.map((p) => (
            <Badge key={p} variant="glass" className="text-[9px] uppercase">{p}</Badge>
          ))}
        </div>

        <div
          className="flex items-center gap-1 text-[11px] font-medium pt-2 border-t border-border/30"
          style={{ color: meta.color }}
        >
          {active ? 'Path focused on graph below' : 'Click to focus on graph'}
          <ArrowRight className={`h-3 w-3 transition-transform ${active ? 'translate-x-1' : 'group-hover:translate-x-1'}`} />
        </div>
      </div>
    </motion.button>
  );
}

// ─────────────────────────────────────────────────
// Live exposure card - used in live (non-demo) mode.
// Renders a single highest-severity finding edge with the source
// asset → finding category arrow. Clicking focuses the source node
// in the Cytoscape canvas.
// ─────────────────────────────────────────────────
interface LiveExposureCardProps {
  path: { id: string; from: string; to: string; severity: string; fromLabel: string; toLabel: string };
  onClick: () => void;
  delay: number;
}

function LiveExposureCard({ path, onClick, delay }: LiveExposureCardProps) {
  const severityColor =
    path.severity === 'critical' ? '#ef4444'
      : path.severity === 'high' ? '#f97316'
        : path.severity === 'medium' ? '#eab308'
          : '#22c55e';
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.32 }}
      whileTap={{ scale: 0.99 }}
      className="group relative text-left rounded-xl glass-3d w-full"
      style={{ '--accent': severityColor } as React.CSSProperties}
    >
      <div
        className="absolute -top-6 -right-6 h-20 w-20 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${severityColor}40 0%, transparent 70%)` }}
      />
      <div className="relative p-4">
        <div className="flex items-center justify-between mb-2">
          <Badge variant={path.severity as 'critical' | 'high' | 'medium' | 'low'} className="text-[10px] uppercase">
            {path.severity}
          </Badge>
          <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[140px]">{path.id.slice(0, 14)}…</span>
        </div>
        <h3 className="text-sm font-display font-semibold mb-2 leading-tight truncate" style={{ color: severityColor }}>
          {path.fromLabel}
        </h3>
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground mb-2">
          <span className="truncate">{path.fromLabel}</span>
          <ArrowRight className="h-3 w-3 shrink-0" />
          <span className="truncate">{path.toLabel}</span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          A live finding edge of severity <strong style={{ color: severityColor }}>{path.severity}</strong> ties this asset to the
          “{path.toLabel}” category. Click to focus the graph on the source node.
        </p>
        <div
          className="flex items-center gap-1 text-[11px] font-medium pt-2 mt-2 border-t border-border/30"
          style={{ color: severityColor }}
        >
          Click to focus on graph
          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </motion.button>
  );
}

// ─────────────────────────────────────────────────
// Compact exposure-path row - used in the live virtualized list (doc 33).
// Replaces the capped top-3 card grid: every path is reachable, scannable,
// and one click focuses the graph on the source asset.
// ─────────────────────────────────────────────────
export function ExposurePathRowView({
  path,
  active,
  onClick,
}: {
  path: ExposurePathRow;
  active: boolean;
  onClick: () => void;
}) {
  const severityColor =
    path.severity === 'critical' ? '#ef4444'
      : path.severity === 'high' ? '#f97316'
        : path.severity === 'medium' ? '#eab308'
          : '#22c55e';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-[52px] w-full flex items-center gap-3 px-3 text-left border-b border-border/30 hover:bg-muted/40 transition-colors group',
        active && 'bg-primary/5',
      )}
    >
      <Badge variant={path.severity as 'critical' | 'high' | 'medium' | 'low'} className="text-[9px] uppercase w-16 justify-center shrink-0">
        {path.severity}
      </Badge>
      <div className="flex-1 min-w-0 flex items-center gap-1.5 text-xs">
        <span className="truncate font-medium max-w-[48%]" style={{ color: severityColor }}>{path.fromLabel}</span>
        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="truncate text-muted-foreground">{path.toLabel}</span>
      </div>
      <code className="hidden md:block text-[10px] text-muted-foreground/70 font-mono truncate max-w-[120px] shrink-0">{path.id.slice(0, 16)}</code>
      <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shrink-0">
        {active ? 'focused' : 'focus'} <ArrowRight className="h-3 w-3" />
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────
// Mini node preview (used in the inspector header)
// ─────────────────────────────────────────────────
function NodePreview({ cat, sev }: { cat: Cat; sev?: Sev }) {
  const c = sev ? SEV_FILL[sev] : { fill: CAT_FILL[cat].fill, rim: CAT_FILL[cat].rim };
  // Map raw hex to HSL tones for the icon-glass --tone variable
  const toneMap: Record<string, string> = {
    '#ef4444': '0 84% 60%',
    '#f97316': '21 94% 55%',
    '#eab308': '48 96% 50%',
    '#22c55e': '142 71% 45%',
    '#3b82f6': '217 91% 60%',
    '#a855f7': '271 81% 66%',
    '#06b6d4': '189 94% 43%',
    '#10b981': '160 84% 39%',
    '#f59e0b': '38 92% 50%',
    '#94a3b8': '215 16% 47%',
  };
  const tone = toneMap[c.fill] ?? '215 16% 47%';
  return (
    <div
      className={`icon-glass h-12 w-12 grid place-items-center shrink-0 ${sev === 'critical' ? 'animate-pulse' : ''}`}
      style={{ '--tone': tone } as React.CSSProperties}
    >
      <Database className="h-5 w-5" style={{ color: c.fill }} />
    </div>
  );
}

// ─────────────────────────────────────────────────
// Interpretation card (auditor-language summary)
// ─────────────────────────────────────────────────
interface InterpretCardProps {
  title: string;
  value: string;
  detail: string;
  tone: 'critical' | 'warn' | 'info';
}

function InterpretCard({ title, value, detail, tone }: InterpretCardProps) {
  const c = tone === 'critical' ? '#ef4444' : tone === 'warn' ? '#f97316' : '#3b82f6';
  return (
    <div
      className="glass-3d p-4"
      style={{ borderLeft: `3px solid ${c}` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="text-2xl font-display tabular my-1" style={{ color: c }}>{value}</div>
      <p className="text-[11px] text-muted-foreground leading-snug">{detail}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────
// Legend (sits inside the graph card)
// ─────────────────────────────────────────────────
function Legend() {
  return (
    <div className="absolute bottom-3 left-3 glass-strong p-3 rounded-md text-[11px] pointer-events-none space-y-2 max-w-xs">
      <div className="font-semibold uppercase tracking-wider text-muted-foreground text-[9px]">Legend</div>
      <div>
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Risk severity (PII assets)</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: 'hsl(var(--card))', border: '3px solid #E2484F' }} />Critical
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: 'hsl(var(--card))', border: '2px solid #E7A13C' }} />High
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: 'hsl(var(--card))', border: '1px solid #747E90' }} />Medium
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: 'hsl(var(--card))', border: '1px solid #4A505A' }} />Low
          </span>
        </div>
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Risky edges</div>
        <div className="space-y-0.5">
          <span className="flex items-center gap-1.5"><span className="h-1 w-8" style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0 5px, transparent 5px 8px)' }} />Public exposure</span>
          <span className="flex items-center gap-1.5"><span className="h-0.5 w-8" style={{ backgroundColor: '#f59e0b' }} />Cross-border transfer</span>
          <span className="flex items-center gap-1.5"><span className="h-0.5 w-8" style={{ backgroundColor: '#06b6d4', boxShadow: '0 0 8px #22d3ee' }} />Focused exposure path</span>
        </div>
      </div>
    </div>
  );
}
